import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProjectContentTree } from '../ProjectContentTree';
import { ManagedKnowledgeList } from '../ManagedKnowledgeList';
import { useActiveProject } from '../../../core/contexts/ProjectContext';
import { resources } from '../../../core/services/resources';
import { isWizardOnlyProject } from '../../../modules/onboarding/projectTypeProfiles';

export const SIDEBAR_WIDTH = 270;
/** Below this breakpoint the sidebar becomes an overlay drawer instead of a
 * permanent column — mirrors the mock's `lg:flex hidden` sidebar treatment. */
const DESKTOP_BREAKPOINT = 'lg' as const;

/** Section heading with the brand-gradient dot used throughout the marketing
 * site's eyebrow labels — kept local since only this sidebar's four sections
 * use it. */
function SectionLabel({
  children,
  count,
  expanded,
  onToggle,
}: {
  children: ReactNode;
  count?: number;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const collapsible = onToggle != null;
  return (
    <Typography
      variant="overline"
      onClick={onToggle}
      sx={{
        px: 2.5,
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        gap: 0.9,
        cursor: collapsible ? 'pointer' : 'default',
        userSelect: collapsible ? 'none' : undefined,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
          flexShrink: 0,
        }}
      />
      <Box component="span" sx={{ flexGrow: 1 }}>
        {children}
        {count != null && ` (${count})`}
      </Box>
      {collapsible &&
        (expanded ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
        ))}
    </Typography>
  );
}

interface AppSidebarProps {
  /** Whether the overlay drawer is open on small viewports. Ignored on desktop. */
  mobileOpen: boolean;
  onClose: () => void;
}

export function AppSidebar({ mobileOpen, onClose }: AppSidebarProps) {
  const { pathname } = useLocation();
  const { activeProject } = useActiveProject();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT));
  const [keyEntitiesExpanded, setKeyEntitiesExpanded] = useState(true);

  const projectId = activeProject?.id;
  const managedTypesQuery = useQuery({
    queryKey: ['project-managed-entity-types', projectId],
    queryFn: () => resources.projectManagedEntityTypesFor(projectId!).list({ pageSize: 100, sort: 'order,id' }),
    enabled: projectId != null,
  });
  const managedTypesCount = managedTypesQuery.data?.data.length ?? 0;
  // The active project carries no type; same key as the project pages, so this shares their cache entry.
  const projectQuery = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => resources.projects.get(projectId!),
    enabled: projectId != null,
  });
  // An idea validation shows only its wizard and its artifacts.
  const wizardOnly = isWizardOnlyProject(projectQuery.data?.projectType);
  // A business validation is worked through its wizard, so it gets no validation flow link.
  const showValidationFlow = !wizardOnly && projectQuery.data?.projectType !== 'BUSINESS_VALIDATION';

  const overviewTo = activeProject ? `/projects/${activeProject.id}` : '/';
  const validationFlowSelected =
    activeProject !== null && pathname === `/projects/${activeProject.id}/validation-flow`;
  const validationWizardSelected =
    activeProject !== null && pathname === `/projects/${activeProject.id}/validation`;
  const overviewSelected =
    activeProject !== null &&
    !validationFlowSelected &&
    !validationWizardSelected &&
    (pathname === `/projects/${activeProject.id}` ||
      pathname.startsWith(`/projects/${activeProject.id}/`));

  return (
    <Drawer
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={isDesktop ? true : mobileOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isDesktop ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'var(--color-bg-2)',
          borderRight: '1px solid',
          borderRightColor: 'divider',
        },
      }}
    >
      {isDesktop && <Toolbar />}
      <Box sx={{ overflow: 'auto', py: 1 }} onClick={isDesktop ? undefined : onClose}>

        {/* Dashboard — project overview */}
        <Box sx={{ mb: 1 }}>
          <SectionLabel>Dashboard</SectionLabel>
          <List dense disablePadding>
            {!wizardOnly && (
              <ListItemButton
                component={RouterLink}
                to={overviewTo}
                selected={overviewSelected}
                sx={{ mx: 1, borderRadius: 1 }}
              >
                <ListItemIcon
                  sx={{ minWidth: 32, color: overviewSelected ? 'primary.main' : 'text.secondary' }}
                >
                  <DashboardOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Project Overview"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: overviewSelected ? 600 : 400,
                  }}
                />
              </ListItemButton>
            )}
            {activeProject && showValidationFlow && (
              <ListItemButton
                component={RouterLink}
                to={`/projects/${activeProject.id}/validation-flow`}
                selected={validationFlowSelected}
                sx={{ mx: 1, borderRadius: 1 }}
              >
                <ListItemIcon
                  sx={{ minWidth: 32, color: validationFlowSelected ? 'primary.main' : 'text.secondary' }}
                >
                  <TimelineOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Validation flow"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: validationFlowSelected ? 600 : 400,
                  }}
                />
              </ListItemButton>
            )}
            {activeProject && (
              <ListItemButton
                component={RouterLink}
                to={`/projects/${activeProject.id}/validation`}
                selected={validationWizardSelected}
                sx={{ mx: 1, borderRadius: 1 }}
              >
                <ListItemIcon
                  sx={{ minWidth: 32, color: validationWizardSelected ? 'primary.main' : 'text.secondary' }}
                >
                  <FactCheckOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Validation wizard"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: validationWizardSelected ? 600 : 400,
                  }}
                />
              </ListItemButton>
            )}
          </List>
        </Box>

        {/* Project content tree (documents) */}
        <Box sx={{ mb: 1 }}>
          <SectionLabel>Artifacts</SectionLabel>
          <ProjectContentTree />
        </Box>

        {/* Project key entities (curated entity types) */}
        {!wizardOnly && (
          <Box sx={{ mb: 1 }}>
            <SectionLabel
              count={activeProject ? managedTypesCount : undefined}
              expanded={keyEntitiesExpanded}
              onToggle={() => setKeyEntitiesExpanded((prev) => !prev)}
            >
              Project key entities
            </SectionLabel>
            {keyEntitiesExpanded && <ManagedKnowledgeList />}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
